import { useState } from 'react';
import { Box } from '@mui/material';
import { Button, Icon, Input, Loader, Typography } from '@sm-dashboard/ui-components';
import { AiAdImageDropzone } from './AiAdImageDropzone';

// Too little text for the AI to extract anything meaningful from.
const MIN_TEXT_LENGTH = 20;

interface AiFillIntroProps {
  isExtracting: boolean;
  onExtractImage: (imageUrl: string) => void;
  onExtractText: (text: string) => void;
  onSkip: () => void;
}

/**
 * First screen of the new-event flow: the shaliach pastes the ad text or uploads
 * the ad image and the AI extracts the details, or skips straight to the manual form.
 */
export const AiFillIntro = ({ isExtracting, onExtractImage, onExtractText, onSkip }: AiFillIntroProps) => {
  const [text, setText] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const busy = isUploading || isExtracting;
  const canSubmit = Boolean(imageUrl) || text.trim().length >= MIN_TEXT_LENGTH;

  // The API extracts from a single source — the uploaded image wins over the text.
  const handleSubmit = () => {
    if (imageUrl) onExtractImage(imageUrl);
    else onExtractText(text.trim());
  };

  return (
    <Box
      sx={{
        maxWidth: 640,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        pb: { xs: 6, md: 0 }
      }}
    >
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '10px',
          p: 2,
          fontSize: 16,
          lineHeight: 1.6
        }}
      >
        אפשר להעלות מודעה על המוקד, או לכתוב באופן חופשי - ואנחנו כבר נחלץ את הפרטים ונציג אותם בצורה
        מסודרת על המפה הארצית באתר חב"ד ישראל. אפשר גם לעבור למסך הבא ולמלא את הפרטים ידנית.
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body" color="text.secondary">
          טקסט חופשי
        </Typography>
        <Input
          multiline
          minRows={3}
          maxRows={8}
          value={text}
          onChange={event => setText(event.target.value)}
          placeholder='לדוגמה (להמחשה בלבד): תהלוכת ל"ג בעומר, יוצאים מהמתנ"ס ב-15:00, חלוקת ממתקים, הגרלה ולהטוטן'
          disabled={busy}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body" color="text.secondary">
          העלאת תמונה
        </Typography>
        <AiAdImageDropzone
          disabled={isExtracting}
          imageUrl={imageUrl}
          onImageChange={setImageUrl}
          onUploadingChange={setIsUploading}
        />
      </Box>

      {isExtracting && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Loader size={16} />
          <Typography variant="body">
            {imageUrl ? 'ה-AI קורא את המודעה ומחלץ את הפרטים...' : 'ה-AI קורא את הטקסט ומחלץ את הפרטים...'}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <Button
          variant="outlined"
          color="inherit"
          shape="square"
          fullWidth={false}
          loading={isExtracting}
          disabled={busy || !canSubmit}
          startIcon={<Icon icon="check" />}
          onClick={handleSubmit}
        >
          חילוץ הפרטים והקמת המוקד
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          shape="square"
          fullWidth={false}
          disabled={busy}
          startIcon={<Icon icon="editOutlined" />}
          onClick={onSkip}
        >
          אני רוצה לערוך בעצמי את הפרטים
        </Button>
      </Box>
    </Box>
  );
};
